import context from '@truckdown/components-systems';
import { IAccountManager, UpdateAddressModel, AccountModel } from "@truckdown/accounts";
import { defineComponent, ref, Ref, computed, onMounted } from "vue";
import { ILookupService, RegionModel } from "@truckdown/systems";
import { showMessage } from "../../Utilities/Utilities";
import validationError from "../../Validation/Error/Error.vue";
import { CompanyAddressValidator } from "../Validation/CompanyAddressValidator/CompanyAddressValidator";

interface IData {
    isLoading: Ref<boolean>,
    account: Ref<AccountModel>,
    update: Ref<UpdateAddressModel>,
    countryCode: Ref<string>,
    addressValidator: Ref<any>,
    selectableRegions: Ref<string[]>,
    allRegions: Ref<RegionModel[]>,
    postalPlaceholder: Ref<string>
};

const getInitialData = function (props: any) {

    var update = {
        accountId: props.account.id,
        line1: props.account.info.address?.line1,
        city: props.account.info.address?.city,
        regionCode: props.account.info.address?.regionCode,
        postalCode: props.account.info.address?.postalCode
    } as UpdateAddressModel;

    return {
        isLoading: ref<boolean>(false),
        account: ref<AccountModel>(props.account),
        update: ref<UpdateAddressModel>(update),
        countryCode: ref<string>(),
        addressValidator: ref<any>(new CompanyAddressValidator(update, "")),
        selectableRegions: ref<string[]>([]),
        allRegions: ref<RegionModel[]>([]),
        postalPlaceholder: ref<string>("ex. A1A 1A1")
    } as IData;
}

const getMethods = function (props: any, data: IData, ctx: any) {
    return {
        cancelClick: function (): void {
            ctx.emit(cancelEventName);
        },
        saveClick: async function (): Promise<void> {
            await save(data, props, ctx);
        },
        changeCountry: function () {
            getRegions(data.countryCode.value, data);
            data.addressValidator.value = new CompanyAddressValidator(data.update.value, data.countryCode.value);
            data.update.value.regionCode = "";
            if (data.countryCode.value == "US") {
                data.postalPlaceholder.value = "ex. 12345";
            } else {
                data.postalPlaceholder.value = "ex. A1A 1A1";
            }
        }
    }
}

const getRegions = async function (country: string | undefined, data: IData) {
    const lookupService = await context.getService<ILookupService>('ILookupService');
    let regionLookupResult = await lookupService.getRegions(country);
    data.allRegions.value = regionLookupResult;
    if (country) {
        regionLookupResult = regionLookupResult.filter(item => item.country == country);
    }
    data.selectableRegions.value = Array.from(new Set(regionLookupResult.map(item => item.code)));
}

const save = async function (data: IData, props: any, ctx: any): Promise<void> {
    if (!data.isLoading.value && data.addressValidator.value.validate()) {
        data.isLoading.value = true;

        try {
            let accountManager = await context.getService<IAccountManager>('IAccountManager');

            var response = await accountManager.updateAddress(data.update.value);

            if (!response || !response.ok) {
                console.log("Error updating company address: " + response.message);
                showMessage("Error updating company address.", "danger", true, true);
            } else {
                showMessage("Company address was succssfully updated.", "success", true, true);
                ctx.emit(updatedEventName, response.data);
            }
        } catch (err) {
            console.error(err);
        } finally {
            data.isLoading.value = false;
        }
    }
}

const updatedEventName = 'update';
const cancelEventName = 'cancel';

export default defineComponent({
    props: {
        account: {
            type: Object as () => AccountModel,
            required: true
        }
    },
    components: {
        'error': validationError,
    },
    emits: [updatedEventName, cancelEventName],
    setup: function (props, ctx) {
        let data = getInitialData(props);
        let methods = getMethods(props, data, ctx);

        onMounted(async () => {
            data.addressValidator.value = new CompanyAddressValidator(data.update.value, data.countryCode.value);
            await getRegions("", data);

            if (data.update.value.regionCode != "") {
                data.countryCode.value = data.allRegions.value.filter(item => item.code.toLowerCase() == data.update.value.regionCode.toLowerCase())[0].country;
                data.addressValidator.value = new CompanyAddressValidator(data.update.value, data.countryCode.value);
            }
        });

        return {
            ...data,
            ...computed,
            ...methods,
        };
    }
});